import React from 'react';
import ErrorBoundary from './ErrorBoundary';

/**
 * Application-level error boundary wrapper
 * Catches all unhandled errors in the app
 */
export const AppErrorBoundary = ({ children }) => (
  <ErrorBoundary
    level="app"
    showRetry={true}
    showHome={false}
    customMessage="The application encountered an unexpected error. Please refresh the page to continue."
    onRetry={() => window.location.reload()}
  >
    {children}
  </ErrorBoundary>
);

/**
 * Page-level error boundary wrapper
 * Isolates page errors without crashing the entire app
 */
export const PageErrorBoundary = ({ children, pageName }) => (
  <ErrorBoundary
    level="page"
    showRetry={true}
    showHome={true}
    customMessage={pageName ? `The ${pageName} page encountered an error while loading.` : undefined}
  >
    {children}
  </ErrorBoundary>
);

/**
 * Feature-level error boundary wrapper
 * Isolates feature errors within a page
 */
export const FeatureErrorBoundary = ({ children, featureName }) => (
  <ErrorBoundary
    level="feature"
    showRetry={true}
    showHome={false}
    customMessage={featureName ? `The ${featureName} feature is temporarily unavailable.` : undefined}
  >
    {children}
  </ErrorBoundary>
);

/**
 * Component-level error boundary wrapper
 * Compact error handling for individual components
 */
export const ComponentErrorBoundary = ({ children, componentName, compact = true }) => (
  <ErrorBoundary
    level="component"
    showRetry={true}
    showHome={false}
    compact={compact}
    customMessage={componentName ? `${componentName} component error` : undefined}
  >
    {children}
  </ErrorBoundary>
);

/**
 * API/Data loading error boundary wrapper
 * Specialized for data fetching components
 */
export const DataErrorBoundary = ({ children, dataType, onRetry }) => (
  <ErrorBoundary
    level="feature"
    showRetry={true}
    showHome={false}
    customMessage={`Failed to load ${dataType || 'data'}. Please try again.`}
    onRetry={onRetry}
  >
    {children}
  </ErrorBoundary>
);

/**
 * Form error boundary wrapper
 * Specialized for form components
 */
export const FormErrorBoundary = ({ children, formName }) => (
  <ErrorBoundary
    level="component"
    showRetry={true}
    showHome={false}
    compact={true}
    customMessage={`${formName || 'Form'} encountered an error. Please refresh and try again.`}
  >
    {children}
  </ErrorBoundary>
);

/**
 * Route error boundary wrapper
 * Handles routing-related errors
 */
export const RouteErrorBoundary = ({ children }) => (
  <ErrorBoundary
    level="page"
    showRetry={false}
    showHome={true}
    customMessage="This page could not be loaded. Please check the URL or return to the home page."
  >
    {children}
  </ErrorBoundary>
);

/**
 * Third-party integration error boundary wrapper
 * For external service integrations (Stripe, etc.)
 */
export const IntegrationErrorBoundary = ({ children, serviceName }) => (
  <ErrorBoundary
    level="feature"
    showRetry={true}
    showHome={false}
    customMessage={`${serviceName || 'External service'} integration is temporarily unavailable. Please try again later.`}
  >
    {children}
  </ErrorBoundary>
);

export default {
  AppErrorBoundary,
  PageErrorBoundary,
  FeatureErrorBoundary,
  ComponentErrorBoundary,
  DataErrorBoundary,
  FormErrorBoundary,
  RouteErrorBoundary,
  IntegrationErrorBoundary
};